import type { NavigationSplash } from "@components/header/types";
import Link from "@components/Link/Link";
import { Paragraph } from "@components/ui/Paragraph/Paragraph";
import type { ReactNode } from "react";

import { extendTVSlot } from "@/lib/utils";
import { useMenuStore } from "@/stores/menuStore";
import { tv, VariantProps } from "tailwind-variants";
import { ContentfulPicture } from "../ContentfulPicture";
import { ContentfulVideo } from "../ContentfulVideo";

export type HighlightProps = HighlightVariants & {
  className?: string | HighlightSlots;
  item?: NavigationSplash;
  locale?: string;
};

type HighlightVariants = VariantProps<typeof highlightVariants>;

type HighlightSlots = Partial<typeof highlightVariants.slots>;

const highlightVariants = tv({
  slots: {
    root: "px-3",
    title: "all-small-caps text-base font-normal uppercase tracking-wider",
    image: "absolute inset-0 h-full w-full",
    media: "relative aspect-square overflow-hidden",
    content: "text-base font-normal",
  },
  variants: {
    compact: {
      true: {
        root: "grid grid-cols-[auto_1fr] auto-rows-auto grid-flow-col-dense gap-x-4 items-center",
        media: "row-span-2 order-first w-20",
        title: "col-start-2 not-has-[~p]:row-span-2 has-[~p]:self-end",
        content: "col-start-2 text-sm nth-[2]:row-span-2 nth-[3]:self-start",
      },
      false: {
        title: "mb-2",
        content: "mt-1.5",
      },
    },
  },
});

type HightlightLinkProps = {
  className?: string;
  children: ReactNode;
  slug?: string | null;
  locale?: string;
};

const HightlightLink = ({ slug, ...props }: HightlightLinkProps) => {
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (slug) {
    return (
      <Link href={slug} onClick={() => setMobileMenuOpen(false)} {...props} />
    );
  }

  return <div {...props} />;
};

const Highlight = ({ item, locale, compact, className }: HighlightProps) => {
  if (!item) return null;

  const {
    desktopImage,
    mobileImage,
    desktopVideo,
    mobileVideo,
    title,
    slug,
    content,
  } = item;

  const {
    root: rootSlot,
    title: titleSlot,
    image: imageSlot,
    media: mediaSlot,
    content: contentSlot,
  } = highlightVariants({ compact });

  return (
    <HightlightLink
      slug={slug}
      locale={locale}
      className={rootSlot({ className: extendTVSlot("root", className) })}
    >
      {title && (
        <Paragraph
          className={titleSlot({ className: extendTVSlot("title", className) })}
        >
          {title}
        </Paragraph>
      )}
      <div
        className={mediaSlot({ className: extendTVSlot("media", className) })}
      >
        <ContentfulPicture
          desktopImage={desktopImage}
          mobileImage={mobileImage}
          className="absolute inset-0 h-full w-full"
          imageProps={{
            sizes: "20em",
            className: imageSlot({
              className: extendTVSlot("image", className),
            }),
          }}
        />
        <ContentfulVideo
          desktopVideo={desktopVideo}
          mobileVideo={mobileVideo}
          className={imageSlot({ className: extendTVSlot("image", className) })}
        />
      </div>
      {content && (
        <Paragraph
          className={contentSlot({
            className: extendTVSlot("content", className),
          })}
        >
          {content}
        </Paragraph>
      )}
    </HightlightLink>
  );
};

Highlight.displayName = "Highlight";

export default Highlight;
